import UserAvatar from '@/components/UI/UserAvatar';
import { TruncatedAvatar } from '@/components/UI/UserAvatar/UserAvatar';
import type { ColorDetail } from '@/constants/colors';
import { _notNil } from '@/littledash.ts';
import type { TaskStatus, TaskV1 } from '@/model/Task.model';
import { DateTimeRenderFormat, DateUtils } from '@/utils/Date.utils.ts';
import { TZDate } from '@date-fns/tz';
import { useMemo } from 'react';
import styles from './DayList.module.scss';
import cn from 'classnames';
import { StatusIcon } from '@/components/Tasks/Calendar/Views/Day/TaskCard.tsx';

interface TaskDayCardProps {
  task: TaskV1;
  colours?: ColorDetail;
  onClick?: () => void;
  maxUserBeforeTruncate?: number;
  narrow?: boolean;
}

const taskStatusTitle: Record<Exclude<TaskStatus, 'to_do'>, string> = {
  done: 'Done',
  in_progress: 'In progress',
  cancelled: 'Cancelled',
} as const;

const DayCard = ({ task, colours, onClick, maxUserBeforeTruncate = 4, narrow }: TaskDayCardProps) => {
  const timezoneData: string | undefined = useMemo(() => {
    const userZonedTaskStart = new TZDate(task.duration.start, DateUtils.timezone());
    const taskZonedTaskStart = new TZDate(task.duration.start, task.timezone);
    if (userZonedTaskStart.getTimezoneOffset() !== taskZonedTaskStart.getTimezoneOffset()) {
      return `${DateUtils.timezoneName(task.duration.start, task.timezone, 'long')} • ${DateUtils.timezoneAbbreviation(task.duration.start, task.timezone)}`;
    }
    return undefined;
  }, [task]);

  const taskDuration = task.duration;

  const isoStartTime = DateUtils.renderDateTime(taskDuration.start, { format: DateTimeRenderFormat.Time });
  const isoEndTime = DateUtils.renderDateTime(taskDuration.end, { format: DateTimeRenderFormat.Time });

  return (
    <div
      className={`${styles['task-day-item']} ${colours ? colours?.background?.class : 'bg-near-white'} ${
        onClick ? 'pointer' : ''
      } ${narrow ? 'flex-wrap pl3' : 'pl4 items-center '} w-100 h-100 pa2 flex justify-between relative`}
      onClick={onClick}
      data-test-component="DayCard"
      data-test-element="container"
      data-testid="day-card"
      data-tooltip-id="global-tooltip-id"
      data-tooltip-content={timezoneData}
    >
      <div
        className={`${styles['task-day-aside-border']} br1 bg-red absolute z-1`}
        style={{ backgroundColor: colours?.color ?? 'white' }}
      />
      <div className="w-100" data-test-component="DayCard" data-test-element="task-title">
        <div className="flex items-center">
          {!narrow && task.status !== 'to_do' ? (
            <div
              className={cn('fw6 f6 pr1', styles.dayCardTaskTitle, {
                [styles.done]: task.status === 'done',
                [styles.inProgress]: task.status === 'in_progress',
                [styles.cancelled]: task.status === 'cancelled',
              })}
            >
              ({taskStatusTitle[task.status]})
            </div>
          ) : null}
          <h3 className="fw5 f6 lh-title near-black truncate">{task.title}</h3>
          {narrow ? (
            <div className="ml-auto" data-testid="task-status-icon">
              <StatusIcon status={task.status}></StatusIcon>
            </div>
          ) : null}
        </div>
        {narrow ? (
          <div>
            <p className="lh-copy fw5 f7 dark-gray">
              {isoStartTime} - {isoEndTime}
            </p>
          </div>
        ) : null}
        <div className="flex flex-row justify-start items-center">
          <p className="lh-copy fw5 f7 dark-gray truncate">{task.study.name}</p>
          {!narrow && _notNil(task?.target?.animal_count) ? (
            <>
              <span className="ml2 lh-copy fw5 f6 near-black">&middot;</span>
              <p className="ml2 lh-copy fw5 f6 near-black">
                Affects {task.target.animal_count} animal{task.target.animal_count > 1 ? 's' : ''}
              </p>
            </>
          ) : null}
        </div>
      </div>
      <div className={`flex ${narrow ? 'w-100 ui__overflow__scroll_y' : ''}`}>
        {(task.assignees ?? []).slice(0, maxUserBeforeTruncate).map((user, index) => (
          <UserAvatar
            key={user.id}
            tooltip={user.name}
            user={{ name: user.name }}
            className={`${narrow ? 'mr1' : 'ml1'}`}
            dataTestId={`user-avatar-${index}`}
          />
        ))}
        {(task.assignees?.length ?? 0) > maxUserBeforeTruncate && (
          <TruncatedAvatar
            remainder={(task.assignees?.length ?? 0) - maxUserBeforeTruncate}
            className={`${narrow ? 'mr1' : 'ml1'}`}
            dataTestId="truncated-avatar"
          />
        )}
      </div>
    </div>
  );
};

export default DayCard;
